import { create } from "zustand";
import { buildApiUrl } from "@/lib/api";

export interface AuditLogEntry {
	id: string;
	createdAt: string;
	model: string;
	action: string;
	actorType?: string | null;
	actorIdentifier?: string | null;
	browser?: string | null;
	success: boolean;
}

interface PageInfo {
	limit: number;
	hasMore: boolean;
	count: number;
	nextCursor?: string | null;
	total?: number;
	order?: "asc" | "desc";
}

interface FiltersState {
	targetId: string;
	success?: boolean;
	actorType?: string;
	model?: string;
	action?: string;
}

type FetchDirection = "initial" | "next" | "prev" | "current";

interface AuditLogState {
	loading: boolean;
	error?: string;
	entries: AuditLogEntry[];
	pageInfo?: PageInfo;
	filters: FiltersState;
	models: string[];
	modelsLoading: boolean;
	actions: string[];
	actionsLoading: boolean;
	cursorStack: (string | null)[];
	currentCursor: string | null;
	pageIndex: number;
	setTargetId: (value: string) => void;
	setSuccess: (value?: boolean) => void;
	setActorType: (value?: string) => void;
	setModel: (value?: string) => void;
	setAction: (value?: string) => void;
	resetFilters: () => void;
	fetchEntries: (direction?: FetchDirection) => Promise<void>;
	fetchModels: (search?: string) => Promise<void>;
	fetchActions: (search?: string) => Promise<void>;
}

const DEFAULT_LIMIT = 15;

export const useAuditLogStore = create<AuditLogState>((set, get) => ({
	loading: false,
	error: undefined,
	entries: [],
	pageInfo: undefined,
	filters: {
		targetId: "",
		success: undefined,
		actorType: undefined,
		model: undefined,
		action: undefined,
	},
	models: [],
	modelsLoading: false,
	actions: [],
	actionsLoading: false,
	cursorStack: [],
	currentCursor: null,
	pageIndex: 0,
	setTargetId: (value) => {
		set((state) => ({
			filters: {
				...state.filters,
				targetId: value,
			},
		}));
	},
	setSuccess: (value) => {
		set((state) => ({
			filters: {
				...state.filters,
				success: value,
			},
		}));
	},
	setActorType: (value) => {
		set((state) => ({
			filters: {
				...state.filters,
				actorType: value,
			},
		}));
	},
	setModel: (value) => {
		set((state) => ({
			filters: {
				...state.filters,
				model: value,
			},
		}));
	},
	setAction: (value) => {
		set((state) => ({
			filters: {
				...state.filters,
				action: value,
			},
		}));
	},
	resetFilters: () => {
		set({
			filters: {
				targetId: "",
				success: undefined,
				actorType: undefined,
				model: undefined,
				action: undefined,
			},
			cursorStack: [],
			currentCursor: null,
			pageIndex: 0,
		});
	},
	fetchEntries: async (direction = "initial") => {
		const previousState = {
			cursorStack: get().cursorStack,
			currentCursor: get().currentCursor,
			pageIndex: get().pageIndex,
		};

		const currentPageInfo = get().pageInfo;
		let cursorParam: string | undefined;

		if (direction === "next") {
			const nextCursor = currentPageInfo?.nextCursor ?? undefined;
			if (!nextCursor) {
				return;
			}

			const stack = [...previousState.cursorStack, previousState.currentCursor];
			set({
				cursorStack: stack,
				currentCursor: nextCursor,
				pageIndex: previousState.pageIndex + 1,
			});
			cursorParam = nextCursor;
		} else if (direction === "prev") {
			if (!previousState.cursorStack.length) {
				return;
			}

			const history = [...previousState.cursorStack];
			const previousCursor = history.pop() ?? null;
			set({
				cursorStack: history,
				currentCursor: previousCursor,
				pageIndex: Math.max(previousState.pageIndex - 1, 0),
			});
			cursorParam = previousCursor ?? undefined;
		} else if (direction === "current") {
			cursorParam = previousState.currentCursor ?? undefined;
		} else {
			set({
				cursorStack: [],
				currentCursor: null,
				pageIndex: 0,
			});
			cursorParam = undefined;
		}

		set({ loading: true, error: undefined });

		const params = new URLSearchParams({
			limit: String(DEFAULT_LIMIT),
			order: "desc",
			includeTotal: "true",
		});

		const { filters } = get();

		if (filters.targetId.trim()) {
			params.set("identifier", filters.targetId.trim());
		}

		if (typeof filters.success === "boolean") {
			params.set("success", filters.success ? "true" : "false");
		}

		if (filters.actorType) {
			params.set("actorType", filters.actorType);
		}

		if (filters.model) {
			params.set("model", filters.model);
		}

		if (filters.action) {
			params.set("action", filters.action);
		}

		if (cursorParam) {
			params.set("cursor", cursorParam);
		}

		try {
			const response = await fetch(
				buildApiUrl(`/api/audit-logs?${params.toString()}`),
				{
					credentials: "include",
				}
			);

			if (response.status === 401 || response.status === 403) {
				set({
					loading: false,
					entries: [],
					error: "Session expired. Please sign in again.",
				});
				return;
			}

			const payload = await response.json();

			if (!response.ok) {
				throw new Error(
					typeof payload?.message === "string"
						? payload.message
						: "Unable to load audit entries."
				);
			}

			const fetchedItems = payload?.data?.items ?? [];
			const newPageInfo = payload?.data?.pageInfo;

			set({
				loading: false,
				entries: fetchedItems,
				pageInfo: newPageInfo,
				error: undefined,
			});
		} catch (error) {
			set({
				loading: false,
				entries: [],
				error:
					error instanceof Error
						? error.message
						: "Failed to retrieve audit entries.",
			});
		}
	},
	fetchModels: async (search) => {
		set({ modelsLoading: true });
		try {
			const params = new URLSearchParams();
			if (search && search.trim()) {
				params.set("search", search.trim());
			}
			const path = params.toString()
				? `/api/audit-logs/models?${params.toString()}`
				: "/api/audit-logs/models";
			const response = await fetch(buildApiUrl(path), {
				credentials: "include",
			});
			if (!response.ok) {
				const payload = await response.json().catch(() => null);
				throw new Error(
					typeof payload?.message === "string"
						? payload.message
						: "Unable to load models."
				);
			}
			const payload = await response.json();
			const models = Array.isArray(payload?.data?.models)
				? (payload.data.models as string[])
				: [];
			set({ models, modelsLoading: false });
		} catch (error) {
			set({ modelsLoading: false, models: [] });
		}
	},
	fetchActions: async (search) => {
		set({ actionsLoading: true });
		try {
			const params = new URLSearchParams();
			if (search && search.trim()) {
				params.set("search", search.trim());
			}
			const path = params.toString()
				? `/api/audit-logs/actions?${params.toString()}`
				: "/api/audit-logs/actions";
			const response = await fetch(buildApiUrl(path), {
				credentials: "include",
			});
			if (!response.ok) {
				const payload = await response.json().catch(() => null);
				throw new Error(
					typeof payload?.message === "string"
						? payload.message
						: "Unable to load actions."
				);
			}
			const payload = await response.json();
			const actions = Array.isArray(payload?.data?.actions)
				? (payload.data.actions as string[])
				: [];
			set({ actions, actionsLoading: false });
		} catch (error) {
			set({ actionsLoading: false, actions: [] });
		}
	},
}));
